import { PublicLayout } from '../components/layouts/Layout'
import { Card } from '../components/common/UI'

export default function Privacy() {
  return (
    <PublicLayout>
      <div className="min-h-screen bg-gray-50 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">BlastTime Privacy Policy and Data Processing Agreement</h1>
            <p className="text-xl text-gray-600">Last Updated: September 17, 2025</p>
          </div>

          <Card className="p-8 mb-8">
            <div className="prose max-w-none">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Our Commitment to Privacy</h2>
              <p className="text-gray-700 mb-6">
                At BlastTime, we believe your personal information should stay personal. This Privacy Policy and Data Processing Agreement describes how we collect, use, and protect your information while providing our social connection and ride-sharing features through our licensed software application.
              </p>
              <p className="text-gray-700 mb-6">
                This policy applies to all users of the BlastTime mobile application and related services provided by Xupoli Technologies, Inc., a New Jersey corporation.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4">EULA Data Processing Framework</h2>
              
              <h3 className="text-xl font-semibold text-gray-800 mb-3">Software Data Collection</h3>
              <p className="text-gray-700 mb-4">As part of our End User License Agreement (EULA), BlastTime collects and processes data necessary for:</p>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-1">
                <li>Application Functionality: Core software operations and feature delivery</li>
                <li>License Compliance: Ensuring proper use of our licensed software</li>
                <li>Software Updates: Delivering security patches, bug fixes, and new features</li>
                <li>Technical Support: Providing user assistance and troubleshooting</li>
                <li>Platform Integrity: Maintaining software security and preventing unauthorized use</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">Licensed Software Data Rights</h3>
              <p className="text-gray-700 mb-6">By accepting our EULA and using BlastTime, you acknowledge that:</p>
              <ul className="list-disc list-inside text-gray-700 mb-6 space-y-1">
                <li>Data processing is essential for licensed software functionality</li>
                <li>We have the right to collect technical data necessary for software operation</li>
                <li>Software analytics help us improve the licensed application</li>
                <li>Compliance monitoring ensures adherence to license terms</li>
              </ul>

              <h2 className="text-2xl font-bold text-gray-900 mb-4">Information We Collect</h2>
              
              <h3 className="text-xl font-semibold text-gray-800 mb-3">Account Information</h3>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-1">
                <li><strong>Phone Number:</strong> Used exclusively for two-factor authentication and account security</li>
                <li><strong>Profile Information:</strong> Details you provide when creating your account</li>
                <li><strong>User-Generated Content:</strong> Posts, messages, and content you create within our app</li>
                <li><strong>Authentication Data:</strong> Login credentials and session information for license verification</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">Location Data</h3>
              <p className="text-gray-700 mb-4">We collect and use location information to provide core app features:</p>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-1">
                <li><strong>Live Location Sharing:</strong> For connections and ride-sharing functionality</li>
                <li><strong>Trip Coordination:</strong> To facilitate ride-sharing between users</li>
                <li><strong>Event & Group Features:</strong> To help you find and participate in local activities</li>
                <li><strong>Geographic Service Delivery:</strong> Ensuring software features work in your region</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">Contact Information</h3>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-1">
                <li><strong>Address Book:</strong> If you choose to upload contacts, we use this solely to help you find friends already using BlastTime</li>
                <li><strong>Connection Data:</strong> Information about your connections within the app</li>
                <li><strong>Communication Preferences:</strong> Settings for notifications and app communications</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">Device and Technical Information</h3>
              <ul className="list-disc list-inside text-gray-700 mb-6 space-y-1">
                <li><strong>Device Identifiers:</strong> Unique identifiers for license management and software delivery</li>
                <li><strong>Operating System Data:</strong> Version and compatibility information for software optimization</li>
                <li><strong>App Performance Metrics:</strong> Usage statistics and crash reports for software improvement</li>
                <li><strong>Network Information:</strong> Connection data necessary for app functionality</li>
                <li><strong>Software Version Data:</strong> Tracking for update delivery and compatibility</li>
              </ul>

              <h2 className="text-2xl font-bold text-gray-900 mb-4">How We Use Your Information</h2>
              <p className="text-gray-700 mb-4">Your information is used exclusively within the BlastTime ecosystem to provide:</p>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">Core Software Features</h3>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-1">
                <li><strong>Trips:</strong> Coordinate and share rides with other users</li>
                <li><strong>Events:</strong> Discover and participate in local events</li>
                <li><strong>Clubs:</strong> Join interest-based communities</li>
                <li><strong>Groups:</strong> Connect with friends and like-minded individuals</li>
                <li><strong>Live Location:</strong> Share real-time location for safety and coordination during rides</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">Software Operations and EULA Compliance</h3>
              <ul className="list-disc list-inside text-gray-700 mb-6 space-y-1">
                <li><strong>License Management:</strong> Ensuring proper use of our licensed software</li>
                <li><strong>Software Updates:</strong> Delivering automatic updates and patches</li>
                <li><strong>Technical Support:</strong> Providing user assistance and troubleshooting</li>
                <li><strong>Performance Optimization:</strong> Improving software functionality and user experience</li>
                <li><strong>Security Monitoring:</strong> Detecting and preventing unauthorized software use</li>
              </ul>

              <h2 className="text-2xl font-bold text-gray-900 mb-4">What We DON'T Do</h2>
              
              <h3 className="text-xl font-semibold text-gray-800 mb-3">No Third-Party Data Sharing</h3>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-1">
                <li><strong>Zero External Data Sales:</strong> We never sell your personal information to third parties</li>
                <li><strong>No External Services:</strong> Your location, contacts, and personal data stay within BlastTime</li>
                <li><strong>No Third-Party Analytics:</strong> We don't share your data with external analytics services</li>
                <li><strong>Privacy-First Advertising:</strong> Any advertising is handled internally without sharing personal data</li>
                <li><strong>No License Subletting:</strong> We don't share software licensing data with unauthorized parties</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">Data Protection</h3>
              <ul className="list-disc list-inside text-gray-700 mb-6 space-y-1">
                <li>Your live location is only shared with users you choose to connect with</li>
                <li>Contact information remains private unless you decide to share it</li>
                <li>User-generated content stays within the BlastTime community</li>
                <li>Software usage data is aggregated and anonymized when used for improvement</li>
              </ul>

              <h2 className="text-2xl font-bold text-gray-900 mb-4">Data Retention and EULA Compliance</h2>
              
              <h3 className="text-xl font-semibold text-gray-800 mb-3">Retention Periods</h3>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-1">
                <li><strong>Account Data:</strong> Retained while your account is active and for legitimate business purposes</li>
                <li><strong>Connection History:</strong> Connections you make remain in our system even if you delete your account (for safety and continuity of our ride-sharing features)</li>
                <li><strong>Software Licensing Data:</strong> Retained as long as necessary for EULA compliance and legal requirements</li>
                <li><strong>Technical Data:</strong> Performance and usage data retained for software improvement purposes</li>
                <li><strong>Legal Compliance Data:</strong> Retained as required by applicable laws and regulations</li>
              </ul>

              <h3 className="text-xl font-semibold text-gray-800 mb-3">Your Rights and Controls</h3>
              <ul className="list-disc list-inside text-gray-700 mb-6 space-y-1">
                <li><strong>Access:</strong> Request a copy of your personal data</li>
                <li><strong>Correction:</strong> Update or correct inaccurate information</li>
                <li><strong>Deletion:</strong> Request account and data deletion</li>
                <li><strong>Portability:</strong> Export your data in standard formats</li>
                <li><strong>Communication Preferences:</strong> Opt out of marketing emails</li>
              </ul>

              <h2 className="text-2xl font-bold text-gray-900 mb-4">Security Measures</h2>
              <p className="text-gray-700 mb-4">We implement comprehensive security measures to protect your information:</p>
              
              <h3 className="text-xl font-semibold text-gray-800 mb-3">Technical Safeguards</h3>
              <ul className="list-disc list-inside text-gray-700 mb-4 space-y-1">
                <li><strong>Encryption:</strong> Secure data transmission and storage using industry-standard encryption</li>
                <li><strong>Access Controls:</strong> Limited access to personal data on a need-to-know basis</li>
                <li><strong>Software Security:</strong> Regular security audits and vulnerability assessments</li>
                <li><strong>Data Centers:</strong> Secure, monitored facilities for data storage</li>
                <li><strong>Network Protection:</strong> Firewalls and intrusion detection systems</li>
              </ul>

              <h2 className="text-2xl font-bold text-gray-900 mb-4">International Data Transfers</h2>
              <p className="text-gray-700 mb-6">
                BlastTime operates globally with data centers primarily in the United States. If you are located outside the US, your information may be transferred to and processed in the US. We ensure appropriate safeguards are in place for international data transfers in compliance with applicable privacy laws.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4">Children's Privacy</h2>
              <p className="text-gray-700 mb-6">
                BlastTime is not intended for users under 13 years old. We do not knowingly collect personal information from children under 13. If we become aware that we have collected such information, we will take steps to delete it promptly.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4">Contact Information</h2>
              <p className="text-gray-700 mb-6">
                For questions about this privacy policy or to exercise your data rights:
              </p>
              <div className="bg-gray-50 p-4 rounded-lg mb-6">
                <p className="text-gray-700 mb-2">
                  <strong>Email:</strong> <a href="mailto:hello@xupoli.com" className="text-blue-600 hover:text-blue-800">hello@xupoli.com</a>
                </p>
                <p className="text-gray-700 mb-2">
                  <strong>Privacy Officer:</strong> <a href="mailto:hello@xupoli.com" className="text-blue-600 hover:text-blue-800">hello@xupoli.com</a>
                </p>
                <p className="text-gray-700">
                  <strong>Mailing Address:</strong> Xupoli Technologies, Inc., New Jersey, United States
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4">Updates to This Policy</h2>
              <p className="text-gray-700 mb-6">
                We may update this privacy policy from time to time. We will notify you of any material changes by email and through our platform. Your continued use of BlastTime after the effective date of any changes constitutes acceptance of the updated policy.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4">Governing Law</h2>
              <p className="text-gray-700 mb-6">
                This Privacy Policy and Data Processing Agreement is governed by the laws of the State of New Jersey and the United States of America. Any disputes related to privacy or data processing will be resolved in accordance with our Terms of Service and EULA dispute resolution procedures.
              </p>

              <div className="border-t border-gray-200 pt-6 mt-8">
                <p className="text-sm text-gray-500 text-center">
                  © 2025 BlastTime. All Rights Reserved<br />
                  Powered by: Xupoli Technologies Inc.<br />
                  Licensed Software Application - Privacy Policy Version 1.0
                </p>
              </div>
            </div>
          </Card>
        </div>
      </div>
    </PublicLayout>
  )
}